import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useContext } from 'react';
import { AuthContext } from '../../../contexts/AuthContext';
import axios from '../../../api/axios';
import { GENERATE_DESCRIPTION_PROMPT } from './constants';
export function useNotebookChat() {
  const { notebookId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, logout } = useContext(AuthContext);
  const isTeacher = currentUser?.role === 'teacher';

  const [notebookTitle, setNotebookTitle] = useState('');
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [sending, setSending] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('history');
  const [chatWidthMode, setChatWidthMode] = useState('wide');
  const [outlineDialogOpen, setOutlineDialogOpen] = useState(false);
  const [notebookData, setNotebookData] = useState(null);
  const [generatedDescriptionForSave, setGeneratedDescriptionForSave] = useState(null);
  const [savingDescription, setSavingDescription] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [selectingNextQuestion, setSelectingNextQuestion] = useState(false);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [reviewSessionProgress, setReviewSessionProgress] = useState([]);

  const messagesEndRef = useRef(null);
  const autoSendRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`/notes/${notebookId}`).then((res) => {
      if (!cancelled) {
        if (res.data?.outline?.notebook_title) setNotebookTitle(res.data.outline.notebook_title);
        setNotebookData(res.data);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [notebookId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingConversations(true);
    axios.get(`/notes/${notebookId}/chat/conversations`)
      .then((res) => {
        if (!cancelled) setConversations(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setConversations([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingConversations(false);
      });
    return () => { cancelled = true; };
  }, [notebookId]);

  useEffect(() => {
    if (currentConversationId == null) {
      setMessages([]);
      setLoadingHistory(false);
      return;
    }
    setLoadingHistory(true);
    setCurrentExerciseIndex(0); // 切换会话时从第一题开始
    axios.get(`/notes/${notebookId}/chat/conversations/${currentConversationId}`)
      .then((res) => {
        setMessages(filterInternalMessages(res.data.messages || []));
      })
      .catch(() => setMessages([]))
      .finally(() => setLoadingHistory(false));
  }, [notebookId, currentConversationId]);

  // 过滤掉提交反馈时发给 Agent 的内部 prompt（以【当前正在做的题目】开头），避免展示给用户
  const filterInternalMessages = (msgs) => {
    if (!Array.isArray(msgs)) return msgs;
    return msgs.filter((m) => {
      if (m.role !== 'user') return true;
      const c = (m.content || '').trim();
      return !c.startsWith('【当前正在做的题目】');
    });
  };

  const getBaseURL = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    if (process.env.NODE_ENV === 'production') return '/api';
    const { protocol, hostname } = window.location;
    const port = 5001;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return protocol === 'https:' ? '/api' : `${protocol}//${hostname}:${port}/api`;
    }
    return `${protocol}//localhost:${port}/api`;
  };

  // 根据 example 构建题目上下文（题干 + 选项/代码 + 正确答案）
  const buildQuestionContext = (ex) => {
    let ctx = ex?.question ? ex.question.trim().slice(0, 4000) : '';
    const optionsArr = (() => {
      let o = ex?.options;
      if (o != null && typeof o === 'string') {
        try { return JSON.parse(o); } catch (_) { return null; }
      }
      return Array.isArray(o) ? o : null;
    })();
    if (Array.isArray(optionsArr) && optionsArr.length > 0) {
      ctx += '\n【题目选项】\n' + optionsArr.map((opt, i) => `${String.fromCharCode(65 + i)}. ${(opt || '').trim().slice(0, 600)}`).join('\n');
      const correct = ex?.correctAnswer ?? ex?.correct_answer;
      if (correct != null && String(correct).trim()) ctx += `\n【正确答案】${String(correct).trim()}\n`;
    }
    return ctx;
  };

  // 提交后请求 Agent 生成反馈（解释/鼓励/下一题），不追加用户消息，只追加 AI 回复
  const requestAgentFeedbackForSubmission = async (example, answer, result, extraContext = '', onMessagesUpdated = null) => {
    const questionContext = buildQuestionContext(example);
    const isCorrect = result?.isCorrect;

    let instruction;
    if (isCorrect) {
      instruction = '请简短夸奖学生（一两句话，如：做得好、完全正确等），语气温暖。然后说明将进入下一题，鼓励继续。';
    } else {
      instruction = '请先安慰学生（如：差一点、别灰心、我们找到了你的问题、你在进步等），语气温暖。然后讲解本题关键点（为什么错、正确思路）。最后询问学生是否需要类似题目练习。格式要求：数学符号请直接使用 Unicode 字符，例如析取用 ∨、合取用 ∧、否定用 ¬，不要使用 LaTeX（如 \\vee、\\wedge），以免在页面显示时被拆行或错位。';
    }

    const messageToSend = questionContext
      ? `【当前正在做的题目】\n${questionContext}\n【用户刚提交的答案】\n${answer || '（无）'}\n【提交结果】${isCorrect ? '通过' : '未通过'}${extraContext ? `\n【额外信息】${extraContext}` : ''}\n【/当前题目】\n\n${instruction}`
      : `用户刚提交了答案：${answer || '（无）'}，结果：${isCorrect ? '通过' : '未通过'}。${extraContext ? `\n额外信息：${extraContext}` : ''}\n\n${instruction}`;

    setSending(true);
    setStreamingContent('');
    const token = localStorage.getItem('token');
    const url = `${getBaseURL()}/notes/${notebookId}/chat`;
    // 不传 conversationId，避免污染用户会话；后端会新建临时会话，我们不保存其 id，也不会加载它
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
      body: JSON.stringify({ message: messageToSend }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setMessages((prev) => [...prev, { role: 'assistant', content: '抱歉，请求失败：' + (err.error || res.statusText) }]);
      setSending(false);
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let acc = '';
    let fullText = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const lines = acc.split('\n');
        acc = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.delta != null) {
                fullText += data.delta;
                setStreamingContent((c) => c + data.delta);
              }
              // 不更新 currentConversationId，避免 useEffect 拉取会话时把内部 prompt 当成用户消息展示
            } catch (e) { /* ignore */ }
          }
        }
      }
    } finally {
      setStreamingContent('');
      const feedbackMsg = { role: 'assistant', content: fullText.trim() || '无回复内容' };
      setMessages((prev) => {
        const next = [...prev, feedbackMsg];
        onMessagesUpdated?.(next);
        return next;
      });
      setSending(false);
    }
  };

  const sendMessage = async (messageText) => {
    const text = (messageText ?? inputMessage.trim()).trim();
    if (!text || sending) return;

    if (!messageText) setInputMessage('');
    if (!text?.includes('生成一段 50-100 字的描述')) setGeneratedDescriptionForSave(null);
    const userMsg = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setSending(true);
    setStreamingContent('');

    const conversationId = currentConversationId || undefined;
    const token = localStorage.getItem('token');
    const url = `${getBaseURL()}/notes/${notebookId}/chat`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
      },
      body: JSON.stringify({ message: text, conversationId }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: '抱歉，请求失败：' + (err.error || res.statusText),
      }]);
      setSending(false);
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let acc = '';
    let newConversationId = currentConversationId;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const lines = acc.split('\n');
        acc = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.delta != null) {
                setStreamingContent((c) => c + data.delta);
              }
              if (data.done && data.conversationId) {
                newConversationId = data.conversationId;
                setStreamingContent('');
                if (data.conversationHistory?.length) {
                  setMessages(filterInternalMessages(data.conversationHistory));
                  const hist = data.conversationHistory;
                  const lastUser = hist.filter((m) => m.role === 'user').pop();
                  const lastAssistant = hist.filter((m) => m.role === 'assistant').pop();
                  const isGenerateDescRequest = lastUser?.content?.includes('生成一段 50-100 字的描述') || lastUser?.content === GENERATE_DESCRIPTION_PROMPT;
                  if (isGenerateDescRequest && lastAssistant?.content && isTeacher) {
                    setGeneratedDescriptionForSave(lastAssistant.content.trim());
                  }
                }
                setConversations((prev) => {
                  const exists = prev.some((c) => c.id === data.conversationId);
                  if (exists) return prev;
                  return [{
                    id: data.conversationId,
                    questionId: `notebook_qa_${notebookId}_${data.conversationId}`,
                    questionTitle: text.substring(0, 50) + (text.length > 50 ? '...' : ''),
                    preview: text.substring(0, 80),
                    messageCount: (data.conversationHistory?.length) || 0,
                    lastMessageAt: new Date().toISOString(),
                    createdAt: new Date().toISOString(),
                  }, ...prev];
                });
              }
            } catch (e) { /* ignore */ }
          }
        }
      }
    } finally {
      if (newConversationId != null) setCurrentConversationId(newConversationId);
      setStreamingContent('');
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') return prev;
        const full = prev.reduce((a, m) => (m.role === 'assistant' ? a + m.content : a), '');
        const newPrev = [...prev, { role: 'assistant', content: full || '无回复内容' }];
        const isGenerateDescRequest = text?.includes('生成一段 50-100 字的描述') || text === GENERATE_DESCRIPTION_PROMPT;
        if (isGenerateDescRequest && full && isTeacher) {
          setGeneratedDescriptionForSave(full.trim());
        }
        return newPrev;
      });
      setSending(false);
    }
  };

  const handleSaveDescriptionToNotebook = async () => {
    if (!generatedDescriptionForSave || !notebookTitle || savingDescription) return;
    setSavingDescription(true);
    try {
      await axios.put(`/notes/${notebookId}`, {
        title: notebookTitle,
        description: generatedDescriptionForSave,
      });
      setGeneratedDescriptionForSave(null);
      if (notebookData?.outline) {
        setNotebookData({ ...notebookData, outline: { ...notebookData.outline, notebook_description: generatedDescriptionForSave } });
      }
    } catch (err) {
      console.error('Save description failed:', err);
      alert(err.response?.data?.error || '保存失败，请重试');
    } finally {
      setSavingDescription(false);
    }
  };

  const handleNewChat = () => {
    setCurrentConversationId(null);
    setMessages([]);
    setStreamingContent('');
    setGeneratedDescriptionForSave(null);
  };

  const handleSelectConversation = (id) => {
    setCurrentConversationId(id);
    setStreamingContent('');
    setGeneratedDescriptionForSave(null);
  };

  const handleDeleteConversation = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('确定要删除这条对话吗？')) return;
    try {
      await axios.delete(`/notes/${notebookId}/chat/conversations/${id}`);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (currentConversationId === id) {
        setCurrentConversationId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Delete conversation failed:', err);
    }
  };

  const handleGenerateDescription = () => {
    sendMessage(GENERATE_DESCRIPTION_PROMPT);
  };

  const PRAISE_MESSAGES = [
    '做得好！完全正确！',
    '太棒了！答对了！',
    '厉害！继续保持！',
    '完全正确！你真棒！',
    '答对了！做得很好！',
  ];

  // 选择题/代码追踪题/代码题：组件内提交后回调
  const handleExerciseAnswerSubmitted = async (answer, result, extraContext = '') => {
    const lastEx = [...messages].reverse().find((m) => m.type === 'exercise' && m.example);
    if (!lastEx?.example) return;
    const isMultipleChoice = lastEx.example.questionType === 'multiple_choice';
    const isCorrect = result?.isCorrect;
    let nextSessionProgress = reviewSessionProgress;
    if (lastEx._fromReview) {
      let conceptTags = [];
      try {
        const ct = lastEx.example.conceptTags;
        conceptTags = Array.isArray(ct) ? ct : (typeof ct === 'string' ? JSON.parse(ct || '[]') : []);
      } catch (_) {}
      nextSessionProgress = [...reviewSessionProgress, { exampleId: lastEx.example.id, isCorrect, conceptTags }];
      setReviewSessionProgress(nextSessionProgress);
    }
    let baseMsgsForNext = null; // 拉下一题前的消息（含夸奖）
    if (isMultipleChoice && isCorrect) {
      const praise = PRAISE_MESSAGES[Math.floor(Math.random() * PRAISE_MESSAGES.length)];
      const praiseMsg = { role: 'assistant', content: praise };
      const newMsgs = [...messages, praiseMsg];
      baseMsgsForNext = newMsgs;
      setMessages(newMsgs);
      if (lastEx._fromReview && currentConversationId) {
        saveReviewToHistory(newMsgs, currentConversationId, '复习').catch(() => {});
      }
    } else {
      const msgRef = { current: null };
      await requestAgentFeedbackForSubmission(lastEx.example, answer, result, extraContext, (updatedMsgs) => {
        if (lastEx._fromReview && currentConversationId && updatedMsgs?.length) {
          msgRef.current = updatedMsgs;
          saveReviewToHistory(updatedMsgs, currentConversationId, '复习').catch(() => {});
        }
      });
      if (isCorrect && lastEx._fromReview) {
        await new Promise((r) => setTimeout(r, 2200));
        await appendNextReviewQuestion(nextSessionProgress, msgRef.current);
      }
      return;
    }
    if (isCorrect && lastEx._fromReview) {
      await new Promise((r) => setTimeout(r, 2200)); // 让用户看清夸奖/反馈后再选题
      await appendNextReviewQuestion(nextSessionProgress, baseMsgsForNext);
    }
  };

  // 开始复习：拉取题目并追加到当前聊天，不跳转；sessionProgress 为本次复习已完成的题目；baseMessages 为拉下一题前的消息（含夸奖等）
  const appendNextReviewQuestion = async (sessionProgress = [], baseMessages = null) => {
    setSelectingNextQuestion(true);
    try {
      const res = sessionProgress.length > 0
        ? await axios.post(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/review/start`, { sessionProgress })
        : await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/review/start`);
      const { action, exampleId, message, sectionTitle } = res.data;
      // 后端已把 reason + first_message 合并到 message，直接使用
      const introText = message;

      if (action === 'message_only') {
        setMessages((prev) => [...prev, { role: 'assistant', content: message }]);
        return;
      }

      if (action === 'do_question' && exampleId) {
        const introMsg = { role: 'assistant', content: introText };
        setMessages((prev) => [...prev, introMsg]);
        try {
          const exRes = await axios.get(`/notebook-examples/${exampleId}`);
          const example = exRes.data;
          const exerciseMsg = { role: 'assistant', type: 'exercise', example, _fromReview: true, _sectionTitle: sectionTitle };
          setMessages((prev) => [...prev, exerciseMsg]);
          const base = baseMessages ?? messages;
          const newMsgs = [...base, introMsg, exerciseMsg];
          await saveReviewToHistory(newMsgs, currentConversationId, '复习');
        } catch (err) {
          setMessages((prev) => [...prev, { role: 'assistant', content: '题目加载失败，请稍后重试。' }]);
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: err.response?.data?.error || '获取下一题失败，请稍后重试。',
      }]);
    } finally {
      setSelectingNextQuestion(false);
    }
  };

  const saveReviewToHistory = async (msgs, convId = null, title = '复习记录') => {
    try {
      const { data } = await axios.post(`/notes/${notebookId}/chat/conversations`, {
        conversationId: convId,
        messages: msgs,
        title,
      });
      if (!convId && data.conversationId) {
        setCurrentConversationId(data.conversationId);
        const listRes = await axios.get(`/notes/${notebookId}/chat/conversations`);
        setConversations(listRes.data || []);
      }
      return data.conversationId;
    } catch (e) {
      console.warn('Save review to history failed:', e);
      return convId;
    }
  };

  const handleStartReview = async () => {
    if (reviewLoading || sending) return;
    setReviewLoading(true);
    setReviewSessionProgress([]);
    setMessages([]); // 新复习会话，清空
    setCurrentConversationId(null);
    try {
      const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}/review/start`);
      const { action, exampleId, message, sectionTitle } = res.data;
      const introText = message;

      if (action === 'message_only') {
        const newMsgs = [{ role: 'assistant', content: message }];
        setMessages(newMsgs);
        await saveReviewToHistory(newMsgs, null, '复习');
        setReviewLoading(false);
        return;
      }

      if (action === 'do_question' && exampleId) {
        const introMsg = { role: 'assistant', content: introText };
        setMessages((prev) => [...prev, introMsg]);
        try {
          const exRes = await axios.get(`/notebook-examples/${exampleId}`);
          const example = exRes.data;
          const exerciseMsg = {
            role: 'assistant',
            type: 'exercise',
            example,
            _fromReview: true,
            _sectionTitle: sectionTitle,
          };
          setMessages((prev) => [...prev, exerciseMsg]);
          const newMsgs = [introMsg, exerciseMsg];
          await saveReviewToHistory(newMsgs, null, '复习');
        } catch (err) {
          setMessages((prev) => [...prev, { role: 'assistant', content: '题目加载失败，请稍后重试。' }]);
        }
      }
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: err.response?.data?.error || '请求失败，请稍后重试。',
      }]);
    } finally {
      setReviewLoading(false);
    }
  };

  // 复习题索引：优先 _fromReview，否则包含所有题目（历史会话也支持上一题/下一题）
  const reviewExerciseIndices = (() => {
    const fromReview = messages
      .map((m, i) => (m.type === 'exercise' && m.example && m._fromReview ? i : -1))
      .filter((i) => i >= 0);
    if (fromReview.length > 0) return fromReview;
    return messages
      .map((m, i) => (m.type === 'exercise' && m.example ? i : -1))
      .filter((i) => i >= 0);
  })();
  // 新题加入时自动切到最后一题
  const prevReviewCountRef = useRef(0);
  useEffect(() => {
    const n = reviewExerciseIndices.length;
    if (n > prevReviewCountRef.current) {
      setCurrentExerciseIndex(n - 1);
      prevReviewCountRef.current = n;
    } else if (n > 0) {
      prevReviewCountRef.current = n;
    } else {
      prevReviewCountRef.current = 0;
    }
  }, [reviewExerciseIndices.length]);
  // 当前展示的题目（复习模式下一题一页，否则取最后一道）
  const lastExercise = reviewExerciseIndices.length > 0
    ? messages[reviewExerciseIndices[Math.min(currentExerciseIndex, reviewExerciseIndices.length - 1)]]
    : [...messages].reverse().find((m) => m.type === 'exercise' && m.example);
  const lastExerciseAnswerInInput = lastExercise?.example && (
    ['short_answer', 'proof'].includes(lastExercise.example.questionType) ||
    (lastExercise.example.questionType === 'code_tracing' && !(lastExercise.example.options && lastExercise.example.options.length > 0))
  );
  // 代码题：左侧展示题目，右侧展示代码编辑器；可开关
  const lastExerciseCode = lastExercise?.example?.questionType === 'code' ? lastExercise : null;
  const [codeEditorOpen, setCodeEditorOpen] = useState(true);

  useEffect(() => {
    if (lastExerciseCode) setCodeEditorOpen(true);
  }, [lastExerciseCode]);

  const handleSend = async (textToSend) => {
    const text = (textToSend ?? inputMessage.trim()).trim();
    if (!text || sending) return;

    if (lastExerciseAnswerInInput) {
      // 填空题/简答题/证明题：输入框内容视为作答，由 Agent 生成反馈
      if (!textToSend) setInputMessage('');
      const ex = lastExercise.example;
      const fromReview = lastExercise._fromReview;
      setMessages((prev) => [...prev, { role: 'user', content: text }]);
      setSending(true);
      try {
        const res = await axios.post(`/notebook-examples/${ex.id}/submit`, { answer: text });
        const sub = res.data.submission;
        const isCorrect = sub.status === 'passed';
        let nextSessionProgress = reviewSessionProgress;
        if (fromReview) {
          let conceptTags = [];
          try {
            const ct = ex.conceptTags;
            conceptTags = Array.isArray(ct) ? ct : (typeof ct === 'string' ? JSON.parse(ct || '[]') : []);
          } catch (_) {}
          nextSessionProgress = [...reviewSessionProgress, { exampleId: ex.id, isCorrect, conceptTags }];
          setReviewSessionProgress(nextSessionProgress);
        }
        const msgRef = { current: null };
        await requestAgentFeedbackForSubmission(ex, text, { isCorrect, score: sub.score }, '', (updatedMsgs) => {
          if (fromReview && currentConversationId && updatedMsgs?.length) {
            msgRef.current = updatedMsgs;
            saveReviewToHistory(updatedMsgs, currentConversationId, '复习').catch(() => {});
          }
        });
        if (isCorrect && fromReview) {
          await new Promise((r) => setTimeout(r, 2200));
          await appendNextReviewQuestion(nextSessionProgress, msgRef.current);
        }
      } catch (err) {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: '提交失败：' + (err.response?.data?.error || err.message),
        }]);
      } finally {
        setSending(false);
      }
      return;
    }

    await sendMessage(textToSend ?? undefined);
  };

  useEffect(() => {
    if (!isTeacher || sending || streamingContent) return;
    if (messages.length >= 2) {
      const last = messages[messages.length - 1];
      const prev = messages[messages.length - 2];
      const isGenerateDescRequest = prev?.role === 'user' && (
        prev?.content?.includes('生成一段 50-100 字的描述') || prev?.content === GENERATE_DESCRIPTION_PROMPT
      );
      if (isGenerateDescRequest && last?.role === 'assistant' && last?.content?.trim()) {
        setGeneratedDescriptionForSave(last.content.trim());
        setTimeout(() => scrollToBottom(), 100);
        return;
      }
    }
    setGeneratedDescriptionForSave(null);
  }, [messages, sending, streamingContent, isTeacher]);

  useEffect(() => {
    const autoSend = location.state?.autoSend;
    if (autoSend && !autoSendRef.current) {
      autoSendRef.current = true;
      navigate(location.pathname, { replace: true, state: {} });
      setTimeout(() => sendMessage(autoSend), 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅在地进入带 autoSend 时执行一次
  }, [location.state?.autoSend, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return {
    notebookId,
    navigate,
    logout,
    currentUser,
    isTeacher,
    notebookTitle,
    agentAvatar: notebookData?.agentAvatar,
    conversations,
    currentConversationId,
    messages,
    inputMessage,
    setInputMessage,
    loadingHistory,
    loadingConversations,
    sending,
    streamingContent,
    sidebarCollapsed,
    setSidebarCollapsed,
    sidebarTab,
    setSidebarTab,
    chatWidthMode,
    setChatWidthMode,
    outlineDialogOpen,
    setOutlineDialogOpen,
    notebookData,
    generatedDescriptionForSave,
    savingDescription,
    messagesEndRef,
    sendMessage,
    handleSaveDescriptionToNotebook,
    handleNewChat,
    handleSelectConversation,
    handleDeleteConversation,
    handleGenerateDescription,
    handleStartReview,
    reviewLoading,
    selectingNextQuestion,
    reviewExerciseIndices,
    currentExerciseIndex,
    setCurrentExerciseIndex,
    handleExerciseAnswerSubmitted,
    handleSend,
    lastExercise,
    lastExerciseAnswerInInput,
    lastExerciseCode,
    codeEditorOpen,
    setCodeEditorOpen,
    formatDate,
  };
}
